import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { forkJoin } from 'rxjs';
import { MyOskAgreementsService } from '../../../../api/api-services/my-osk-agreements.service';
import { MyOskCoursesService } from '../../../../api/api-services/my-osk-courses.service';
import { IMyOskAgreementVm } from '../../../../api/my-osk-agreement-vm.model';
import { IMyOskCourseVm } from '../../../../api/my-osk-course-vm.model';
import { OskCoursePaymentType } from '../../../../api/osk-course-payment-type.model';
import { SortOrder } from '../../../../api/sort-order.model';
import { formatPln } from '../../helpers/price-format';

export interface AgreementSelection {
  agreementGuid: string | null;
  courseGuid: string | null;
}

/**
 * Course and agreement of a student, from the active ones of the OSK. Only those of the student's
 * category (or for any category) are offered; choosing a course preselects its agreement.
 */
@Component({
  selector: 'app-agreement-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, DropdownModule],
  templateUrl: './agreement-picker.component.html',
  styleUrl: './agreement-picker.component.scss',
})
export class AgreementPickerComponent implements OnInit, OnChanges {
  @Input() examCategoryGuid: string | null | undefined = null;
  /** A choice made earlier, restored when the picker is shown again. */
  @Input() initialSelection: AgreementSelection | null = null;
  @Output() selectionChange = new EventEmitter<AgreementSelection>();

  private readonly agreementsService = inject(MyOskAgreementsService);
  private readonly coursesService = inject(MyOskCoursesService);

  public agreements: IMyOskAgreementVm[] = [];
  public courses: IMyOskCourseVm[] = [];
  public agreementGuid: string | null = null;
  public courseGuid: string | null = null;
  public loaded = false;
  public loadFailed = false;

  get hasAgreements(): boolean {
    return this.agreements.length > 0;
  }

  get agreementOptions(): { label: string; value: string }[] {
    return this.agreements
      .filter((agreement) => this.matchesCategory(agreement.examGuid))
      .map((agreement) => ({
        label: agreement.examName ? `${agreement.name} (kat. ${agreement.examName})` : agreement.name,
        value: agreement.guid,
      }));
  }

  get courseOptions(): { label: string; value: string }[] {
    return this.courses
      .filter((course) => this.matchesCategory(course.examGuid))
      .map((course) => ({ label: `${course.name} - ${this.courseTerms(course)}`, value: course.guid }));
  }

  ngOnInit(): void {
    forkJoin([
      this.agreementsService.get(false, '', 'name', 1, 200, SortOrder.Ascending),
      this.coursesService.get(false, '', 'name', 1, 200, SortOrder.Ascending),
    ]).subscribe({
      next: ([agreements, courses]) => {
        this.agreements = agreements.data ?? [];
        this.courses = courses.data ?? [];
        this.loaded = true;
        this.agreementGuid = this.initialSelection?.agreementGuid ?? null;
        this.courseGuid = this.initialSelection?.courseGuid ?? null;
        this.dropChoicesOutsideCategory();
        this.emit();
      },
      error: () => {
        this.loaded = true;
        this.loadFailed = true;
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['examCategoryGuid'] && !changes['examCategoryGuid'].firstChange && this.loaded) {
      this.dropChoicesOutsideCategory();
      this.emit();
    }
  }

  onCourseChange(): void {
    const course = this.courses.find((item) => item.guid === this.courseGuid);
    if (course?.agreementGuid && this.agreementOptions.some((option) => option.value === course.agreementGuid)) {
      this.agreementGuid = course.agreementGuid;
    }
    this.emit();
  }

  emit(): void {
    this.selectionChange.emit({ agreementGuid: this.agreementGuid, courseGuid: this.courseGuid });
  }

  private courseTerms(course: IMyOskCourseVm): string {
    const price = formatPln(course.price);
    if (course.paymentType !== OskCoursePaymentType.Installments) return `${price}, jednorazowo`;
    const count = course.installments.length;
    return `${price}, ${count} ${count === 1 ? 'rata' : count < 5 ? 'raty' : 'rat'}`;
  }

  private matchesCategory(examGuid?: string): boolean {
    return !examGuid || !this.examCategoryGuid || examGuid === this.examCategoryGuid;
  }

  private dropChoicesOutsideCategory(): void {
    if (this.courseGuid && !this.courseOptions.some((option) => option.value === this.courseGuid)) {
      this.courseGuid = null;
    }
    if (this.agreementGuid && !this.agreementOptions.some((option) => option.value === this.agreementGuid)) {
      this.agreementGuid = null;
    }
  }
}
